import React, { useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { initialState } from "./initialState";

export const posteriorSortArray = ["Vitreous", "Fundus"];
export const posteriorHeaders = ["Posterior Segment", "R-Eye", "L-Eye"];

interface PosteriorSegmentProps {
  segmentData: typeof initialState;
  onSegmentChange: (updatedData: typeof initialState) => void;
}

const COLS = [
  { key: "rEye", label: "R-Eye" },
  { key: "lEye", label: "L-Eye" }
] as const;

export default function PosteriorSegment({
  segmentData,
  onSegmentChange
}: PosteriorSegmentProps): React.JSX.Element {
  const [newRowLabel, setNewRowLabel] = useState("");

  const handleChange = (row: string, col: string, value: string) => {
    const updated = produce(segmentData, (draft: typeof initialState) => {
      if (!draft.posteriorSegment) {
        draft.posteriorSegment = JSON.parse(JSON.stringify(initialState.posteriorSegment));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData = draft.posteriorSegment[
        row as keyof typeof draft.posteriorSegment
      ] as unknown as Record<string, string | null>;
      if (rowData) {
        rowData[col] = value === "" ? null : value;
      }
    });
    onSegmentChange(updated);
  };

  const handleAddNewRow = () => {
    if (newRowLabel.trim() === "") return;
    const updated = produce(segmentData, (draft: typeof initialState) => {
      const posterior = draft.posteriorSegment as Record<
        string,
        { rEye: string | null; lEye: string | null }
      >;
      posterior[newRowLabel] = { rEye: null, lEye: null };
    });
    onSegmentChange(updated);
    setNewRowLabel("");
  };

  const currentPosteriorSegment = segmentData?.posteriorSegment || {};

  const sortRows = (keys: string[]) =>
    [...keys].sort((a, b) => {
      const indexA = posteriorSortArray.indexOf(a);
      const indexB = posteriorSortArray.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const rows = sortRows(Object.keys(currentPosteriorSegment));

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <Box>
      <TableContainer>
        <Typography
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: "#ededed",
            borderLeft: 1,
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          Posterior Segment
        </Typography>

        <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
              {COLS.map((col) => (
                <TableCell key={col.key} sx={headerCellStyle}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row}>
                <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
                {COLS.map((col) => (
                  <TableCell key={col.key} sx={cellStyle}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={
                        (currentPosteriorSegment as Record<string, Record<string, string | null>>)[
                          row
                        ]?.[col.key] ?? ""
                      }
                      onChange={(e) => handleChange(row, col.key, e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "4px 8px",
                          fontSize: "0.875rem"
                        }
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          placeholder="Add new"
          value={newRowLabel}
          onChange={(e) => setNewRowLabel(e.target.value)}
          sx={{ width: "150px" }}
        />
        <IconButton onClick={handleAddNewRow} color="primary" size="small">
          <AddCircleOutlineIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
